/**
 * Permissions store for NeoTIM
 * Uses Zustand with immer middleware for immutable updates
 *
 * This store holds raw permission state and provides scope-aware permission checking.
 * For capability derivation and user type classification, see lib/permissions/capabilities.ts
 */

import { STORAGE_KEYS } from "@lib/config/storageKeys";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import {
  isModuleWildcard,
  isWildcard,
  matchesPermission,
  parsePermission,
} from "../permissions/parser";
import type { PermissionsSummary } from "../permissions/summary.types";
import type { PermissionContext } from "../permissions/types";

/**
 * DTO received from the backend API
 */
export interface UserPermissionsDto {
  readonly permissions: readonly string[];
  readonly hasWildcard: boolean;
  readonly context: PermissionContext;
  readonly summary: PermissionsSummary | null;
}

/**
 * Default empty context
 */
const defaultContext: PermissionContext = {
  filiale_code: "",
  dept_path: "",
  is_manager: false,
  is_dept_head: false,
  personne_id: "",
  managed_count: 0,
  accessible_filiales: [],
  profile_name: null,
  is_active: true,
};

/**
 * Check if user department path is a descendant of target path (ltree-style)
 */
function isDescendantPath(childPath: string, parentPath: string): boolean {
  if (!childPath || !parentPath) return false;
  return childPath.startsWith(`${parentPath}.`);
}

/**
 * Store interface
 */
export interface PermissionsStore {
  // State
  /** Raw permission strings granted to the user */
  permissions: readonly string[];
  /** Whether the user has a wildcard permission (*) */
  hasWildcard: boolean;
  /** User's permission context (filiale, dept, etc.) */
  context: PermissionContext;
  /** Pre-computed permissions summary for O(1) lookups */
  summary: PermissionsSummary | null;

  // Actions
  /** Set permissions from API response */
  setPermissions: (dto: UserPermissionsDto) => void;
  /** Clear all permissions (logout) */
  clear: () => void;

  // Permission checking
  /** Check if user has a specific permission */
  can: (permission: string) => boolean;
  /** Check if user has any of the specified permissions */
  canAny: (permissions: string[]) => boolean;
  /** Check if user has all of the specified permissions */
  canAll: (permissions: string[]) => boolean;

  // Summary-based O(1) checks
  /** Check if user has access to a module (O(1) via summary) */
  hasModuleAccess: (module: string) => boolean;
  /** Check if user has a specific action on a resource (O(1) via summary) */
  hasAction: (module: string, resource: string, action: string) => boolean;
  /** Get user type from summary */
  getUserType: () => string | null;

  // Scope-aware checking
  /** Check if permission has self scope and user can access own data */
  canSelf: (permission: string) => boolean;
  /** Check if permission has managed scope and user is a manager */
  canManage: (permission: string) => boolean;
  /** Check if user's department allows access to target department */
  canInDepartment: (permission: string, targetDeptPath?: string) => boolean;
  /** Check if user's filiale allows access to target filiales */
  canInFiliale: (permission: string, targetFilialeCode?: string) => boolean;
}

/**
 * Permissions store with persistence
 * Persists to localStorage under 'neotim_permissions' key
 */
export const usePermissionStore = create<PermissionsStore>()(
  persist(
    immer((set, get) => ({
      permissions: [] as readonly string[],
      hasWildcard: false,
      context: defaultContext,
      summary: null,

      setPermissions: (dto: UserPermissionsDto) =>
        set((state) => {
          state.permissions = [...dto.permissions];
          state.hasWildcard = dto.hasWildcard;
          state.context = {
            ...dto.context,
            accessible_filiales: [...dto.context.accessible_filiales],
          } as any;
          state.summary = dto.summary
            ? ({
                ...dto.summary,
                modules: [...dto.summary.modules],
                // Legacy localStorage-persisted summaries lack this field — never null-crash.
                modules_scoped: [...(dto.summary.modules_scoped ?? [])],
                module_actions: dto.summary.module_actions,
                filiales: [...dto.summary.filiales],
                scopes: [...dto.summary.scopes],
              } as any)
            : null;
        }),

      clear: () =>
        set((state) => {
          state.permissions = [];
          state.hasWildcard = false;
          state.context = defaultContext as any;
          state.summary = null;
        }),

      // Basic permission checking
      can: (permission: string) => {
        const state = get();
        if (state.hasWildcard) return true;
        if (!permission) return false;

        // Use matchesPermission parser which correctly handles qualifier suffixes
        // (e.g., 'rh.*:filiale:*' matches 'rh.conges.view')
        return state.permissions.some((grant) =>
          matchesPermission(grant, permission, state.context),
        );
      },

      canAny: (permissions: string[]) => {
        const state = get();
        if (state.hasWildcard) return true;
        if (!permissions || permissions.length === 0) return false;
        return permissions.some((p) => get().can(p));
      },

      canAll: (permissions: string[]) => {
        const state = get();
        if (state.hasWildcard) return true;
        if (!permissions || permissions.length === 0) return true;
        return permissions.every((p) => get().can(p));
      },

      // Summary-based O(1) checks
      hasModuleAccess: (module: string) => {
        const state = get();
        if (state.hasWildcard) return true;
        if (!state.summary) return state.can(`${module}.*`);
        return state.summary.modules.includes(module);
      },

      hasAction: (module: string, resource: string, action: string) => {
        const state = get();
        if (state.hasWildcard) return true;
        if (!state.summary) return state.can(`${module}.${resource}.${action}`);
        const moduleResources = state.summary.module_actions[module];
        if (!moduleResources) return false;
        const resourceActions = moduleResources[resource] || moduleResources["*"];
        if (!resourceActions) return false;
        return resourceActions.includes(action) || resourceActions.includes("*");
      },

      getUserType: () => {
        const state = get();
        return state.summary?.user_type || null;
      },

      // Scope-aware checking
      canSelf: (permission: string) => {
        const state = get();
        if (!state.can(permission)) return false;
        const parsed = parsePermission(permission);
        return parsed.scopeQualifier === "self";
      },

      canManage: (permission: string) => {
        const state = get();
        if (!state.can(permission)) return false;
        const parsed = parsePermission(permission);
        if (parsed.scopeQualifier !== "managed") return false;
        return state.context.is_manager === true;
      },

      canInDepartment: (permission: string, targetDeptPath?: string) => {
        const state = get();
        if (!state.can(permission)) return false;

        const parsed = parsePermission(permission);
        if (!parsed.scopeQualifier && !parsed.filialeQualifier) {
          return true; // No scope restriction
        }

        // Department scope check
        if (parsed.scopeQualifier === "dept" || parsed.scopeQualifier === "dept.*") {
          if (!targetDeptPath && !state.context.dept_path) return false;
          const targetPath = targetDeptPath || state.context.dept_path;
          const userPath = state.context.dept_path;

          if (userPath === targetPath) return true;
          if (isDescendantPath(userPath, targetPath)) return true;
          if (isDescendantPath(targetPath, userPath)) return true;
          return false;
        }

        return true;
      },

      canInFiliale: (permission: string, targetFilialeCode?: string) => {
        const state = get();
        if (!state.can(permission)) return false;

        const parsed = parsePermission(permission);
        const { filialeQualifier } = parsed;

        // No qualifier — no filiale restriction
        if (!filialeQualifier) return true;

        // Wildcard — access to all filiales
        if (filialeQualifier === "*") return true;

        const userFiliale = state.context.filiale_code;

        // 'own' — access limited to user's own filiale
        if (filialeQualifier === "own") {
          if (!userFiliale) return false;
          if (!targetFilialeCode) return true;
          return targetFilialeCode === userFiliale;
        }

        // Specific filiale code (e.g. 'PH') — permission restricted to that filiale
        if (!userFiliale) return false;
        if (filialeQualifier !== userFiliale) return false;
        if (!targetFilialeCode) return true;
        return targetFilialeCode === filialeQualifier;
      },
    })),
    {
      name: STORAGE_KEYS.USER.PERMISSIONS,
      // Only persist the raw data, not derived state
      partialize: (state) => ({
        permissions: state.permissions,
        hasWildcard: state.hasWildcard,
        context: state.context,
        summary: state.summary,
      }),
    },
  ),
);

/**
 * Selector for checking if permissions are loaded
 */
export const selectIsLoaded = (state: PermissionsStore): boolean =>
  state.permissions.length > 0 || state.hasWildcard;

/**
 * Selector for getting permissions array
 */
export const selectPermissions = (state: PermissionsStore): readonly string[] => state.permissions;

/**
 * Selector for getting user context
 */
export const selectContext = (state: PermissionsStore): PermissionContext => state.context;
